/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — shared polyline geometry: line-crossing "hops"
   (circuit-diagram bridges) + the sharp-corner polyline used for hop
   detection AND segment-drag hit testing. Ported behavior-identical
   from the old `edgeHops.ts` (Team Compliance Sprint) — this module
   is the single geometry source the new BaseEdge-based OrgEdgeV2
   feeds into when it has to hand-roll a path (waypoints / hops),
   per the People Rebuild §3 architecture note: native React Flow
   path builders are used whenever there's nothing to hand-roll.

   ORTHOGONAL LINES LAW (People Rebuild, Josh's exact formula
   2026-08-18: "shortest path, fewest turns, but only 90-degree
   perpendicular changes — diagonal lines are impossible; if close
   enough to aligned, snap to a single straight line") — the
   Rounded/Square/Straight/Curved routing SELECTOR is gone. Every edge
   is now one style: squared orthogonal corners, rounded at a fixed
   8px radius. The invariant this file exists to guarantee: EVERY
   rendered segment of EVERY edge is exactly vertical or exactly
   horizontal — `normalizePolyline` below is the ONE function every
   polyline (auto route, manual waypoints, a mid-drag preview) is run
   through immediately before render or hop detection, so a diagonal
   reaching the screen is structurally impossible, not just usually
   avoided.

     - A plain two-point auto pair (no waypoints, not claimed by the
       trunk-and-bus tree router): `buildEdgePoints` + `snapPlainPair`
       near-axis-snap it to ONE straight line when it's within
       `AXIS_SNAP_PX` of vertical/horizontal (absorbing the offset at
       whichever end is "flex" — a junction's small dot center — over
       whichever is "fixed" — a member/team card's real edge-center
       anchor). Otherwise `buildAutoOrthoRoute` computes the
       fewest-turn orthogonal route for the two anchors' exit sides —
       an L (1 turn) when their exit directions permit it, else a Z (2
       turns) through the midpoint of the axis they share.
     - A manual (real-waypoint) or freely turn-dragged polyline can be
       diagonal-adjacent going IN (a raw drag point, or an old-format
       stored waypoint) — `normalizePolyline` elbows any diagonal leg
       before it ever reaches `getEdgePolyline`'s output or a render
       call. `buildWaypointPath` then rounds every REAL interior corner
       to the one surviving `CORNER_RADIUS`.
     - `getEdgePolyline` (the SHARP-corner list used for hit-testing /
       hops / the turn-dot overlay) is `normalizePolyline` itself —
       nothing downstream ever sees an un-normalized point list.
   ═══════════════════════════════════════════════════════════════ */

import { getSmoothStepPath, Position } from '@xyflow/react';
import { parseEndpoint } from './types';

export type Pt = { x: number; y: number };
export type Side = 'top' | 'bottom' | 'left' | 'right';

export interface ObstacleRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which side pair minimizes travel when no handle side is stored —
 *  the default-side rule for a brand-new/auto edge. (Migrated from the
 *  retired `edgeAutoRoute.ts` verbatim — no longer feeds a router, but
 *  still the one shared definition for "which side looks right".) */
export function pickAutoSides(source: Pt, target: Pt): { sourceSide: Side; targetSide: Side } {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy >= 0
      ? { sourceSide: 'bottom', targetSide: 'top' }
      : { sourceSide: 'top', targetSide: 'bottom' };
  }
  return dx >= 0
    ? { sourceSide: 'right', targetSide: 'left' }
    : { sourceSide: 'left', targetSide: 'right' };
}

/** 'flex' = a junction's small dot center, which can absorb a few px
 *  of near-axis snap invisibly. 'fixed' = a member/team card's real
 *  edge-center anchor, which should hold its exact position when the
 *  two ends disagree by less than the snap threshold. */
export type AnchorFlex = 'fixed' | 'flex';

/** Classify an RF node id (`team-box-<id>` / `junction-<id>` / a raw
 *  member id) for the near-axis endpoint-snap tie-break. */
export function endpointFlex(nodeId: string): AnchorFlex {
  return parseEndpoint(nodeId).type === 'junction' ? 'flex' : 'fixed';
}

export const AXIS_SNAP_PX = 8; // px — "within 8px of vertical/horizontal snaps crisp" (Josh law 2026-08-18)

function pickSharedCoord(a: number, b: number, aFlex: AnchorFlex, bFlex: AnchorFlex): number {
  if (aFlex === 'flex' && bFlex !== 'flex') return b;
  if (bFlex === 'flex' && aFlex !== 'flex') return a;
  return (a + b) / 2; // both fixed or both flex — split evenly, neither commits over the other
}

/** Near-axis snap for a plain two-point AUTO segment. If the span is
 *  within `AXIS_SNAP_PX` of pure vertical or pure horizontal, both
 *  endpoints get the SAME shared coordinate so the rendered line is
 *  dead straight instead of a near-miss diagonal (the stair-jog defect
 *  this rework retires) — otherwise the pair passes through unchanged
 *  (diagonal is allowed). */
export function snapPlainPair(a: Pt, b: Pt, aFlex: AnchorFlex, bFlex: AnchorFlex): [Pt, Pt] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  if (absDx <= AXIS_SNAP_PX && absDx <= absDy) {
    const x = pickSharedCoord(a.x, b.x, aFlex, bFlex);
    return [
      { x, y: a.y },
      { x, y: b.y },
    ];
  }
  if (absDy <= AXIS_SNAP_PX && absDy < absDx) {
    const y = pickSharedCoord(a.y, b.y, aFlex, bFlex);
    return [
      { x: a.x, y },
      { x: b.x, y },
    ];
  }
  return [a, b];
}

const ORTHO_EPS = 0.5; // px — treat a segment within this of true V/H as already orthogonal (matches the hop-detection axis-align epsilon below)

/** THE invariant enforcement point (People Rebuild orthogonal-lines
 *  law, 2026-08-18): every rendered/persisted segment of every edge is
 *  exactly vertical or exactly horizontal — diagonal is structurally
 *  impossible. Runs over ANY source polyline (auto route, manual
 *  waypoints, a mid-drag preview) and converts each diagonal segment
 *  into a single elbow, splitting it into two orthogonal sub-segments.
 *  Elbow orientation matches the segment's DOMINANT axis first — the
 *  first sub-leg out of `a` follows whichever of dx/dy is larger, so a
 *  mostly-horizontal diagonal reads "go horizontal, then drop" and a
 *  mostly-vertical one reads "go vertical, then slide". Returns the
 *  SAME array reference when nothing needed splitting (cheap no-op for
 *  the overwhelmingly common already-orthogonal case, preserving the
 *  reference-equality several callers memoize on). */
export function normalizePolyline(points: Pt[]): Pt[] {
  if (points.length < 2) return points;
  let changed = false;
  const out: Pt[] = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.abs(dx) > ORTHO_EPS && Math.abs(dy) > ORTHO_EPS) {
      changed = true;
      const elbow: Pt = Math.abs(dx) >= Math.abs(dy) ? { x: b.x, y: a.y } : { x: a.x, y: b.y };
      out.push(elbow);
    }
    out.push(b);
  }
  return changed ? out : points;
}

/** Pair up every numeric token in an SVG path `d` string built only
 *  from M/L/Q commands (all `getSmoothStepPath` ever emits) into
 *  points, dropping consecutive duplicates — a zero-radius `getBend`
 *  emits a degenerate `Q x,y x,y` at the exact corner, which parses to
 *  the same point three times in a row. */
function parsePathCorners(d: string): Pt[] {
  const nums = d.match(/-?\d+(?:\.\d+)?/g)?.map(Number) ?? [];
  const out: Pt[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const p: Pt = { x: nums[i], y: nums[i + 1] };
    const last = out[out.length - 1];
    if (!last || Math.abs(last.x - p.x) > ORTHO_EPS || Math.abs(last.y - p.y) > ORTHO_EPS) {
      out.push(p);
    }
  }
  return out;
}

/** Fewest-turn orthogonal auto-route between two anchors that are NOT
 *  near-axis-aligned (that case is `snapPlainPair`'s one-straight-line
 *  job) — Josh's exact formula, 2026-08-18: "shortest path, fewest
 *  turns, but only 90-degree perpendicular changes." An L (1 turn)
 *  when the anchors' exit sides permit it, else a Z (2 turns) through
 *  the midpoint of the axis their exit directions share. Delegates the
 *  actual corner math to React Flow's own `getSmoothStepPath` (offset
 *  0, radius 0, so it returns a bare M/L/Q corner list with no stub or
 *  rounding to strip) — this already IS the "classic smoothstep
 *  geometry" the law calls for, and reusing it beats re-deriving its
 *  direction/quadrant cases by hand. Returns MID points only (source
 *  and target excluded), for `buildEdgePoints`'s `mid` slot. */
export function buildAutoOrthoRoute(a: Pt, aPos: Position, b: Pt, bPos: Position): Pt[] {
  const [d] = getSmoothStepPath({
    sourceX: a.x,
    sourceY: a.y,
    sourcePosition: aPos,
    targetX: b.x,
    targetY: b.y,
    targetPosition: bPos,
    borderRadius: 0,
    offset: 0,
  });
  const corners = parsePathCorners(d);
  return corners.length > 2 ? corners.slice(1, -1) : [];
}

/** Build the final [source, ...mid, target] point list for an edge.
 *  Manual/bus-routed edges (`mid` non-empty) pass through unchanged —
 *  `normalizePolyline` downstream is what guarantees THEIR
 *  orthogonality; this is only where the plain 2-point auto case gets
 *  decided: aligned within `AXIS_SNAP_PX` (Josh law, "if close enough
 *  to aligned, snap to a single straight line") collapses to one
 *  straight segment, otherwise `buildAutoOrthoRoute` computes the
 *  fewest-turn L/Z. */
export function buildEdgePoints(
  source: Pt,
  target: Pt,
  mid: Pt[],
  sourceFlex: AnchorFlex,
  targetFlex: AnchorFlex,
  sourcePosition: Position,
  targetPosition: Position
): Pt[] {
  if (mid.length > 0) return [source, ...mid, target];
  const [s, t] = snapPlainPair(source, target, sourceFlex, targetFlex);
  if (Math.abs(s.x - t.x) <= ORTHO_EPS || Math.abs(s.y - t.y) <= ORTHO_EPS) {
    return [s, t];
  }
  const autoMid = buildAutoOrthoRoute(s, sourcePosition, t, targetPosition);
  return [s, ...autoMid, t];
}

/** THE sharp-corner polyline used for hop detection, segment-drag hit
 *  testing, the turn-dot overlay, and (via `buildWaypointPath`) the
 *  actual rendered stroke — `normalizePolyline` itself. `points` is
 *  whatever `buildEdgePoints` assembled (near-axis-snapped 2-point
 *  pair / auto L-Z route / manual waypoints) or a live drag preview;
 *  this is the ONE place every one of those funnels through before hop
 *  detection or render, which is what makes a diagonal reaching the
 *  screen structurally impossible (People Rebuild orthogonal-lines
 *  law, 2026-08-18). Kept as its own named function (not inlined) so
 *  every call site reads the same "this is THE normalize point"
 *  documentation. */
export function getEdgePolyline(points: Pt[]): Pt[] {
  return normalizePolyline(points);
}

export interface EdgeGeometry {
  edgeId: string;
  polyline: Pt[];
}

/** segIndex -> sorted list of X positions (along that horizontal
 *  segment) where a hop bridge should be drawn. */
export type HopsBySegment = Map<number, number[]>;

const CROSS_GUARD = 6; // px — ignore "crossings" this close to either segment's own endpoints (shared corners, not real crossings)

/** O(H × V) where H/V are horizontal/vertical segment COUNTS across
 *  the whole chart (not edges²) — cheap even for a few hundred edges.
 *  Caller memoizes on a geometry signature so this only re-runs when
 *  node/waypoint positions actually change.
 *
 *  `familyOf` (optional, People Rebuild 2026-08-17 trunk-and-bus
 *  routing): maps an edgeId to its tree-route family key (the shared
 *  parent's node id) for edges the bus router produced. Two edges in
 *  the SAME family are intentionally collinear at the shared trunk/bus
 *  points — without this exclusion every multi-child trunk would grow
 *  bridge arcs over its own siblings. In practice the shared busY
 *  already makes every family h/v-segment pair start-or-end exactly at
 *  the bus (which CROSS_GUARD already treats as a shared corner, not a
 *  crossing) — this is a belt-and-suspenders explicit guard, not a
 *  silent reliance on that coincidence. */
export function computeEdgeHops(
  edgeGeoms: EdgeGeometry[],
  familyOf?: Map<string, string>
): Map<string, HopsBySegment> {
  const hSegs: { edgeId: string; segIndex: number; y: number; minX: number; maxX: number }[] = [];
  const vSegs: { edgeId: string; x: number; minY: number; maxY: number }[] = [];

  for (const eg of edgeGeoms) {
    const pts = eg.polyline;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      if (Math.abs(a.y - b.y) < 0.5 && Math.abs(a.x - b.x) >= 0.5) {
        hSegs.push({
          edgeId: eg.edgeId,
          segIndex: i,
          y: a.y,
          minX: Math.min(a.x, b.x),
          maxX: Math.max(a.x, b.x),
        });
      } else if (Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) >= 0.5) {
        vSegs.push({
          edgeId: eg.edgeId,
          x: a.x,
          minY: Math.min(a.y, b.y),
          maxY: Math.max(a.y, b.y),
        });
      }
    }
  }

  const result = new Map<string, HopsBySegment>();
  for (const h of hSegs) {
    for (const v of vSegs) {
      if (v.edgeId === h.edgeId) continue; // never hop over your own line
      if (familyOf) {
        const hFam = familyOf.get(h.edgeId);
        const vFam = familyOf.get(v.edgeId);
        if (hFam && hFam === vFam) continue; // same trunk/bus family — collinear on purpose
      }
      if (v.x <= h.minX + CROSS_GUARD || v.x >= h.maxX - CROSS_GUARD) continue;
      if (h.y <= v.minY + CROSS_GUARD || h.y >= v.maxY - CROSS_GUARD) continue;
      let bySeg = result.get(h.edgeId);
      if (!bySeg) {
        bySeg = new Map();
        result.set(h.edgeId, bySeg);
      }
      const arr = bySeg.get(h.segIndex) || [];
      arr.push(v.x);
      bySeg.set(h.segIndex, arr);
    }
  }
  return result;
}

/** Cheap signature so callers can useMemo hop computation and only
 *  recompute when geometry actually changed. */
export function geometrySignature(edgeGeoms: EdgeGeometry[]): string {
  return edgeGeoms
    .map(
      (e) =>
        `${e.edgeId}:${e.polyline.map((p) => `${Math.round(p.x)},${Math.round(p.y)}`).join('|')}`
    )
    .join(';');
}

const HOP_RADIUS = 7;

/** Sharp-corner polyline renderer that also draws crossing hops (used
 *  by OrgEdgeV2 when an edge actually has a hop). */
export function buildHoppedPath(polyline: Pt[], hopsBySeg?: HopsBySegment): string {
  if (polyline.length < 2) return '';
  let d = `M ${polyline[0].x},${polyline[0].y}`;
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const hopXs = hopsBySeg?.get(i);
    if (!hopXs || hopXs.length === 0) {
      d += ` L ${b.x},${b.y}`;
      continue;
    }
    const goingRight = b.x >= a.x;
    const sorted = [...hopXs].sort((x1, x2) => (goingRight ? x1 - x2 : x2 - x1));
    for (const hx of sorted) {
      const r = Math.min(HOP_RADIUS, Math.abs(b.x - a.x) / 4);
      const startX = goingRight ? hx - r : hx + r;
      const endX = goingRight ? hx + r : hx - r;
      d += ` L ${startX},${a.y}`;
      d += ` A ${r},${r} 0 0 1 ${endX},${a.y}`;
    }
    d += ` L ${b.x},${b.y}`;
  }
  return d;
}

const CORNER_RADIUS = 8; // px — the one surviving "soft corner" radius (People Rebuild, Josh law 2026-08-18: the Rounded/Square/Straight/Curved routing selector is gone, every corner is rounded this much now)

/** Render a [source, ...corners, target] polyline as an SVG path,
 *  rounding every REAL interior corner to `CORNER_RADIUS` — the one
 *  surviving line look. Callers MUST pass an already-`normalizePolyline`'d
 *  point list — every adjacent pair here is assumed orthogonal, which
 *  is what lets the rounding math treat "in leg" / "out leg" as pure
 *  horizontal/vertical trims (no diagonal-leg case left to special-case,
 *  since normalize already elbowed any diagonal out before this runs). */
export function buildWaypointPath(points: Pt[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const next = points[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.min(CORNER_RADIUS, inLen / 2, outLen / 2);
    if (r <= 0.01) {
      d += ` L ${cur.x},${cur.y}`;
      continue;
    }
    const inT = 1 - r / inLen;
    const outT = r / outLen;
    const inPt: Pt = { x: prev.x + (cur.x - prev.x) * inT, y: prev.y + (cur.y - prev.y) * inT };
    const outPt: Pt = { x: cur.x + (next.x - cur.x) * outT, y: cur.y + (next.y - cur.y) * outT };
    d += ` L ${inPt.x},${inPt.y} Q ${cur.x},${cur.y} ${outPt.x},${outPt.y}`;
  }
  d += ` L ${points[points.length - 1].x},${points[points.length - 1].y}`;
  return d;
}

/** Build a single orthogonal segment between two points, respecting
 *  the exit/entry direction implied by each end's handle Position —
 *  used only as the multi-waypoint fallback (the native builder
 *  handles the pure 2-point case). */
export function buildOrthogonalSeg(
  a: Pt,
  b: Pt,
  sourcePos: Position,
  targetPos: Position,
  radius: number
): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) < 0.5 || Math.abs(dy) < 0.5) {
    return `M ${a.x},${a.y} L ${b.x},${b.y}`;
  }
  const sgnX = Math.sign(dx);
  const sgnY = Math.sign(dy);
  const sourceVertical = sourcePos === Position.Top || sourcePos === Position.Bottom;
  const targetVertical = targetPos === Position.Top || targetPos === Position.Bottom;
  if (sourceVertical && targetVertical) {
    const midY = (a.y + b.y) / 2;
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx) / 2, Math.abs(dy) / 2);
      return (
        `M ${a.x},${a.y} L ${a.x},${midY - r * sgnY} Q ${a.x},${midY} ${a.x + r * sgnX},${midY} ` +
        `L ${b.x - r * sgnX},${midY} Q ${b.x},${midY} ${b.x},${midY + r * sgnY} L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${a.x},${midY} L ${b.x},${midY} L ${b.x},${b.y}`;
  }
  if (!sourceVertical && !targetVertical) {
    const midX = (a.x + b.x) / 2;
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx) / 2, Math.abs(dy) / 2);
      return (
        `M ${a.x},${a.y} L ${midX - r * sgnX},${a.y} Q ${midX},${a.y} ${midX},${a.y + r * sgnY} ` +
        `L ${midX},${b.y - r * sgnY} Q ${midX},${b.y} ${midX + r * sgnX},${b.y} L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${midX},${a.y} L ${midX},${b.y} L ${b.x},${b.y}`;
  }
  if (sourceVertical && !targetVertical) {
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx), Math.abs(dy));
      return (
        `M ${a.x},${a.y} L ${a.x},${b.y - r * sgnY} Q ${a.x},${b.y} ${a.x + r * sgnX},${b.y} ` +
        `L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${a.x},${b.y} L ${b.x},${b.y}`;
  }
  if (radius > 0) {
    const r = Math.min(radius, Math.abs(dx), Math.abs(dy));
    return (
      `M ${a.x},${a.y} L ${b.x - r * sgnX},${a.y} Q ${b.x},${a.y} ${b.x},${a.y + r * sgnY} ` +
      `L ${b.x},${b.y}`
    );
  }
  return `M ${a.x},${a.y} L ${b.x},${a.y} L ${b.x},${b.y}`;
}

/** Project an arbitrary flow-space point onto the nearest point of a
 *  polyline, ALSO returning which segment it landed on (`segIndex`,
 *  0-based into `polyline`) — the segment index doubles as the
 *  insertion index into the edge's `waypoints` array, since
 *  `polyline[0]` is the source anchor (not a waypoint): landing on
 *  segment i means "insert a new waypoint at array index i" (People
 *  Rebuild 2026-08-18, "Add turn here"). Falls back to the point
 *  itself / segIndex 0 for a degenerate (empty) polyline. */
export function projectPointOntoPolylineIndexed(
  polyline: Pt[],
  point: Pt
): { point: Pt; segIndex: number } {
  if (polyline.length === 0) return { point, segIndex: 0 };
  if (polyline.length === 1) return { point: polyline[0], segIndex: 0 };
  let best = polyline[0];
  let bestDist = Infinity;
  let bestSeg = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    const proj: Pt = { x: a.x + t * dx, y: a.y + t * dy };
    const dist = Math.hypot(point.x - proj.x, point.y - proj.y);
    if (dist < bestDist) {
      bestDist = dist;
      best = proj;
      bestSeg = i;
    }
  }
  return { point: best, segIndex: bestSeg };
}

/** Project an arbitrary flow-space point onto the nearest point of a
 *  polyline — used by the "Add junction here" menu action to snap a
 *  right-click location onto the edge's actual drawn path (mirrors
 *  where a drag-based split would have landed). Falls back to the
 *  point itself for a degenerate (empty) polyline. */
export function projectPointOntoPolyline(polyline: Pt[], point: Pt): Pt {
  return projectPointOntoPolylineIndexed(polyline, point).point;
}

/** Grab-and-bend a segment of the sharp-corner polyline. `corners` is
 *  [source, ...waypoints, target]; segIndex identifies which pair is
 *  being dragged. Endpoints at index 0/last are node anchors and
 *  never move — dragging a segment touching one inserts a new corner
 *  next to the anchor instead ("waypoints appear where bent"). The
 *  `axis` param is the ONLY thing the pointer delta can move along —
 *  every corner produced by this function differs from its neighbor
 *  by EXACTLY ONE coordinate, which is what makes it structurally
 *  impossible to produce a diagonal segment (People Rebuild §3 fix). */
export function dragSegment(corners: Pt[], segIndex: number, axis: 'H' | 'V', delta: number): Pt[] {
  const a = corners[segIndex];
  const b = corners[segIndex + 1];
  const isSourceAnchor = segIndex === 0;
  const isTargetAnchor = segIndex === corners.length - 2;
  const shift = (p: Pt): Pt =>
    axis === 'H' ? { x: p.x, y: p.y + delta } : { x: p.x + delta, y: p.y };

  const replacement: Pt[] = [];
  if (isSourceAnchor) replacement.push(a);
  replacement.push(shift(a));
  replacement.push(shift(b));
  if (isTargetAnchor) replacement.push(b);

  return [...corners.slice(0, segIndex), ...replacement, ...corners.slice(segIndex + 2)];
}

function nearestCandidate(raw: number, candidates: number[]): number {
  let best = raw;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = Math.abs(raw - c);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

/** Free 2D turn-drag near-axis snap (People Rebuild 2026-08-18, item
 *  2): while a turn is being dragged, if its new position would put
 *  the leg to its PREVIOUS or NEXT corner within `AXIS_SNAP_PX` of
 *  vertical/horizontal, snap that leg's shared coordinate crisp — x
 *  and y are evaluated independently so a turn can snap-vertical to
 *  one neighbor while staying free on the other axis. Caller applies
 *  the (optional) global alignment-guide snap on top of this. */
export function snapTurnPosition(raw: Pt, prev: Pt, next: Pt): Pt {
  const xCandidates: number[] = [];
  if (Math.abs(raw.x - prev.x) <= AXIS_SNAP_PX) xCandidates.push(prev.x);
  if (Math.abs(raw.x - next.x) <= AXIS_SNAP_PX) xCandidates.push(next.x);
  const yCandidates: number[] = [];
  if (Math.abs(raw.y - prev.y) <= AXIS_SNAP_PX) yCandidates.push(prev.y);
  if (Math.abs(raw.y - next.y) <= AXIS_SNAP_PX) yCandidates.push(next.y);
  return {
    x: nearestCandidate(raw.x, xCandidates),
    y: nearestCandidate(raw.y, yCandidates),
  };
}
